import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import Notifications from './Notifications';
import { useUser } from '../context/UserProvider';
import {
  AppBar,
  Container,
  Toolbar,
  Typography,
  Box,
  IconButton,
  MenuItem,
  Tooltip,
  Menu,
  Link,
  styled,
  ButtonGroup,
  Button,
  OutlinedInput,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Logout as LogoutIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { logoutUser } from '../firebase';
import { isAdminUser } from '../utils/isAdminUser';
import { ADMIN_PAGES } from '../utils/adminPages';

const Bar = styled(Toolbar)(({ theme }) => ({
  height: 90,
  borderBottom: `1px solid ${theme.palette.info.light}`,
}));

const NavBar = () => {
  const location = useLocation();
  let navigate = useNavigate();
  const { user: kipUser, firebaseUser, logout } = useUser();
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElAdmin, setAnchorElAdmin] = useState(null);
  const isAdmin = isAdminUser(kipUser);
  const [search, setSearch] = useState('');

  const goToProfile = () => {
    navigate('/profile');
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const pages = [
    { display: 'Sets', url: '/sets' },
    { display: 'Tracks', url: '/tracks' },
    { display: 'Artists', url: '/artists' },
    { display: 'Labels', url: '/labels' },
    { display: 'Genres', url: '/genres' },
  ];
  const settings = [
    { display: 'Profile', action: goToProfile, show: true },
    { display: 'Logout', action: handleLogout, show: true },
  ];

  const appBarStyles = {
    backgroundColor: 'common.black',
    backgroundImage: 'none',
    zIndex: 2,
  };

  function onSearch(event) {
    if (!search) return;
    event?.target?.blur();
    navigate(`/search?s=${search}`);
    setSearch('');
  }

  function onSearchKeyDown(event) {
    if (event.key === 'Enter' && search) {
      onSearch(event);
      event?.target?.blur();
    }
  }

  return (
    <AppBar position="fixed" sx={appBarStyles}>
      <Container maxWidth="xl" sx={{ mb: 1 }}>
        <Bar disableGutters>
          <Box
            onClick={() => navigate('/home')}
            sx={{
              mr: 2,
              display: 'flex',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            <Logo />
          </Box>
          {(firebaseUser || kipUser) && (
            <>
              <OutlinedInput
                type={'text'}
                size="small"
                autoComplete="off"
                sx={{
                  width: { xs: '50%', sm: '70%', md: 'unset' },
                  margin: 'auto',
                  backgroundColor: 'secondary.main',
                }}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={onSearchKeyDown}
                value={search}
                endAdornment={
                  <InputAdornment position="start">
                    <IconButton edge="end" onClick={onSearch}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                <ButtonGroup
                  variant="contained"
                  color={'secondary'}
                  sx={{ marginLeft: '20%' }}
                >
                  {pages.map(({ display, url }) => (
                    <Button
                      onClick={() => navigate(url)}
                      key={url}
                      color={
                        location.pathname.includes(url)
                          ? 'primary'
                          : 'secondary'
                      }
                      sx={{ fontFamily: 'Pixel', height: 35 }}
                    >
                      {display}
                    </Button>
                  ))}
                  {isAdmin && (
                    <Button
                      onClick={(event) => setAnchorElAdmin(event.currentTarget)}
                      color={
                        location.pathname.startsWith('/admin')
                          ? 'primary'
                          : 'secondary'
                      }
                      endIcon={<ArrowDropDownIcon />}
                      aria-controls="menu-admin"
                      aria-haspopup="true"
                      sx={{ fontFamily: 'Pixel', height: 35 }}
                    >
                      Admin
                    </Button>
                  )}
                </ButtonGroup>
                <Menu
                  id="menu-admin"
                  anchorEl={anchorElAdmin}
                  open={Boolean(anchorElAdmin)}
                  onClose={() => setAnchorElAdmin(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  {ADMIN_PAGES.map(({ display, url }) => (
                    <MenuItem
                      key={url}
                      selected={location.pathname.startsWith(url)}
                      onClick={() => {
                        setAnchorElAdmin(null);
                        navigate(url);
                      }}
                    >
                      {display}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>

              <Box
                sx={{
                  flexGrow: 0,
                  display: 'flex',
                  marginLeft: 'auto',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Notifications />
                <Tooltip title="Open menu">
                  <Box
                    sx={{ display: 'flex', cursor: 'pointer' }}
                    onClick={handleOpenUserMenu}
                  >
                    <IconButton
                      sx={{ p: 0 }}
                      size="large"
                      aria-label="account of current user"
                      aria-controls="menu-appbar"
                      aria-haspopup="true"
                      color="inherit"
                    >
                      <MenuIcon />
                    </IconButton>
                  </Box>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      width: 220,
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                      mt: 1.5,
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  }}
                >
                  {pages.map(({ display, url }) => (
                    <MenuItem
                      key={display}
                      onClick={handleCloseUserMenu}
                      sx={{ p: 0 }}
                    >
                      <Link
                        key={display}
                        to={url}
                        href={url}
                        underline="none"
                        width={'100%'}
                        px={2}
                        py={0.75}
                      >
                        {display}
                      </Link>
                    </MenuItem>
                  ))}
                  {isAdmin && <Divider />}
                  {isAdmin && (
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      px={2}
                      display="block"
                    >
                      Admin
                    </Typography>
                  )}
                  {isAdmin &&
                    ADMIN_PAGES.map(({ display, url }) => (
                      <MenuItem
                        key={url}
                        onClick={handleCloseUserMenu}
                        sx={{ p: 0 }}
                      >
                        <Link
                          to={url}
                          href={url}
                          underline="none"
                          width={'100%'}
                          px={2}
                          py={0.75}
                        >
                          {display}
                        </Link>
                      </MenuItem>
                    ))}
                  <Divider />
                  {settings.map(
                    ({ display, action, show }) =>
                      show && (
                        <MenuItem
                          key={display}
                          onClick={() => {
                            handleCloseUserMenu();
                            action();
                          }}
                          sx={{ p: 0 }}
                        >
                          <Typography
                            width={'100%'}
                            color={'primary'}
                            px={2}
                            py={0.75}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {display === 'Logout' && (
                              <LogoutIcon fontSize="small" />
                            )}
                            {display}
                          </Typography>
                        </MenuItem>
                      )
                  )}
                </Menu>
              </Box>
            </>
          )}
        </Bar>
      </Container>
    </AppBar>
  );
};

export default NavBar;
